import {
  addProspectContact,
  addProspectNote,
  setProspectPhoneNumber,
  getProspect,
  getProspectPhoneNumbers,
  deleteProspectPhoneNumber,
  setProspectContactPreferences,
  getProspectAccounts,
  setProspectAddress,
  updateProspect,
  getProspectEvents,
  getProspectPartialOrders,
  getProspectPartialShoppers,
  getProspectShoppers,
  createProspect,
  getProspects,
} from "@apis/prospects/prospects"
import { skipToken, useMutation, useQuery } from "@tanstack/react-query"
import { prospectsKeys } from "./queryKeys"
import { sendProspectEvent } from "./prospects"
import { sortByDate } from "@utils/formatters"
import type { IMemberSearchParams } from "./types"

export const useAddProspectNoteMutation = () => {
  return useMutation({
    mutationFn: addProspectNote,
  })
}

export const useAddProspectContactMutation = () => {
  return useMutation({
    mutationFn: addProspectContact,
  })
}

export const useSetProspectPhoneNumberMutation = () => {
  return useMutation({
    mutationFn: setProspectPhoneNumber,
  })
}

export const useDeleteProspectPhoneNumberMutation = () => {
  return useMutation({
    mutationFn: deleteProspectPhoneNumber,
  })
}

export const useGetProspectQuery = (prospect_guid: string | undefined) => {
  return useQuery({
    queryKey: prospectsKeys.detail(prospect_guid),
    queryFn: prospect_guid ? () => getProspect(prospect_guid) : skipToken,
  })
}

export const useGetProspectPhoneNumbersQuery = (prospect_guid: string) => {
  return useQuery({
    queryKey: prospectsKeys.prospectPhoneNumbers(prospect_guid),
    queryFn: () => getProspectPhoneNumbers(prospect_guid),
  })
}

export const useSetProspectContactPreferencesMutation = () => {
  return useMutation({
    mutationFn: setProspectContactPreferences,
  })
}

export const useSendProspectEventMutation = () => {
  return useMutation({
    mutationFn: sendProspectEvent,
  })
}

export const useGetProspectAccountsQuery = (prospect_guid: string) => {
  return useQuery({
    queryKey: prospectsKeys.prospectAccounts(prospect_guid),
    queryFn: () => getProspectAccounts(prospect_guid),
  })
}

export const useGetProspectEventsQuery = (
  prospect_guid: string | undefined,
  isEnabled: boolean = true,
) => {
  return useQuery({
    queryKey: prospectsKeys.prospectEvents(prospect_guid),
    queryFn:
      isEnabled && prospect_guid
        ? () => getProspectEvents(prospect_guid)
        : skipToken,
  })
}

export const useSetProspectAddressMutation = () => {
  return useMutation({
    mutationFn: setProspectAddress,
  })
}

export const useCreateProspectMutation = () => {
  return useMutation({
    mutationFn: createProspect,
  })
}

export const useUpdateProspectMutation = () => {
  return useMutation({
    mutationFn: updateProspect,
  })
}

export const useGetProspectPartialOrdersQuery = (prospect_guid: string) => {
  return useQuery({
    queryKey: prospectsKeys.prospectPartialOrders(prospect_guid),
    queryFn: () => getProspectPartialOrders(prospect_guid),
    select: (data) => sortByDate(data, "order_dt"),
  })
}

export const useGetProspectPartialShoppersQuery = (prospect_guid: string) => {
  return useQuery({
    queryKey: prospectsKeys.prospectPartialShoppers(prospect_guid),
    queryFn: () => getProspectPartialShoppers(prospect_guid),
    select: (data) => sortByDate(data, "initial_sale_dt"),
  })
}

export const useGetProspectShoppersQuery = (prospect_guid: string) => {
  return useQuery({
    queryKey: [...prospectsKeys.prospectShoppers(prospect_guid)],
    queryFn: () => getProspectShoppers(prospect_guid),
    select: (data) => sortByDate(data, "created_at"),
  })
}

export const useGetProspectsQuery = (
  params: IMemberSearchParams,
  isEnabled: boolean = true,
) => {
  return useQuery({
    queryKey: prospectsKeys.list(JSON.stringify(params)),
    queryFn:
      params.query && isEnabled
        ? ({ signal }) => getProspects({ params, signal })
        : skipToken,
  })
}
